import { useCallback } from 'react';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useFormErrorsActions } from '../../../../styledComponents/FormErrorProvider/FormErrorProvider.hooks';

import { useActions } from '../../../../hooks/useActions';
import { useApiResponseFail } from '../../../../hooks/useApiResponseFail';
import { useNavigateSearch } from '../../../../hooks/useNavigateSearch';
import { useProcessing } from '../../../../hooks/useProcessing';
import {
  ProductSubscriptionOverviewPageParams,
  RoutePath,
} from '../../../../models/enums/RoutePath';
import { IWarningRatification } from '../../../../models/interfaces/WarningRatification';
import { organizationContractsSelector } from '../../../../store/redux-slices/contracts/selectors';
import { updateModalState } from '../../../../store/redux-slices/modals';
import { organizationNameSelector } from '../../../../store/redux-slices/organizations/selectors';
import {
  AA_PRICING_LOADING_PROCESSING,
  ORGANIZATION_CONTRACTS_LOADING_PROCESSING,
  ORGANIZATION_LOADING_PROCESSING,
  SUBSCRIPTIONS_AA_DATES_LOADING_PROCESSING,
  SUBSCRIPTION_EDITING_PROCESSING,
  SUBSCRIPTION_OVERVIEW_LOADING_PROCESSING,
} from '../../../../store/redux-slices/processes/constants';
import { updateProductAASubscription } from '../../../../store/saga-slices/subscriptions/airAmbulance';
import { IUpdateAASubscriptionPayload } from '../../../../store/saga-slices/subscriptions/airAmbulance/models';
import { IValidationErrorResponse } from '../../../../models/ValidationErrorResponse';
import { validateEditProductSubscriptionForm } from '../../Common/editProductSubscriptionForm.validation';
import { EditProductSubscriptionPageParamsType } from '../../../../models/types/RoutePath';
import { editingAASubscriptionDataSelector } from '../../../../store/redux-slices/subscriptions/airAmbulance/selectors';
import { BasicSubscriptionFormFields } from '../../../../styledComponents/SubscriptionFormPage/FormSections/BasicSubscriptionForm/basicSubscriptionForm.models';
import { AddContractModalFormDetails } from '../../../../styledComponents/Modals/AddContractModal/AddContractModalForm/addContractModalForm.models';
import { ADD_CONTRACT_MODAL_NAME } from '../../../../styledComponents/Modals/AddContractModal/addContractModal.const';
import { IEditAAOrganizationSubscriptionModel } from '../../../../models/interfaces/Subscription/AirAmbulance/EditAAOrganizationSubscriptionModel';

export const useEditAASubscriptionForm = () => {
  const navigate = useNavigateSearch();
  const alert = useAlert();

  const { organizationId, subscriptionId, subscriptionType } =
    useParams<EditProductSubscriptionPageParamsType>();
  const { t } = useTranslation();

  const UpdateProductSubscription = useActions(updateProductAASubscription);
  const UpdateModalState = useActions(updateModalState);

  const organizationName = useSelector(organizationNameSelector);
  const organizationContracts = useSelector(organizationContractsSelector);
  const subscriptionData = useSelector(editingAASubscriptionDataSelector);

  const subscriptionEditing = useProcessing(SUBSCRIPTION_EDITING_PROCESSING);

  const dataLoading = useProcessing([
    ORGANIZATION_CONTRACTS_LOADING_PROCESSING,
    AA_PRICING_LOADING_PROCESSING,
    SUBSCRIPTIONS_AA_DATES_LOADING_PROCESSING,
    ORGANIZATION_LOADING_PROCESSING,
    SUBSCRIPTION_OVERVIEW_LOADING_PROCESSING,
  ]);

  const { validateErrors } = useFormErrorsActions();

  const { handleResponse } = useApiResponseFail();

  const handleSubmit = (values: IEditAAOrganizationSubscriptionModel) => {
    updateOrganizationSubscription(values);
  };

  const updateOrganizationSubscription = useCallback(
    (
      values: IEditAAOrganizationSubscriptionModel,
      ratifications: IWarningRatification[] = [],
    ) => {
      const extendedValues: IEditAAOrganizationSubscriptionModel = {
        ...values,
        ratifications,
      };
      const createNewSubscriptionPayload: IUpdateAASubscriptionPayload = {
        organizationId,
        subscriptionId,
        data: extendedValues,
        error: (err: IValidationErrorResponse) => {
          handleResponse({
            err,
            values,
            ratifications,
            alertErrorMessage: t(
              `alerts.${
                values.isDraft ? 'draft-' : ''
              }subscription-update-fail`,
              {
                value: organizationName,
              },
            ),
          });
        },
        success: () => {
          navigate(
            RoutePath.ClientOverviewProductSubscriptionOverviewPageFullPath,
            {
              params: {
                [ProductSubscriptionOverviewPageParams.OrganizationId]:
                  organizationId,
                [ProductSubscriptionOverviewPageParams.SubscriptionId]:
                  subscriptionId,
                [ProductSubscriptionOverviewPageParams.SubscriptionType]:
                  subscriptionType,
              },
            },
          );
          alert.success(
            t(
              `alerts.${
                values.isDraft ? 'draft-' : ''
              }subscription-update-success`,
              {
                value: organizationName,
              },
            ),
          );
        },
      };
      UpdateProductSubscription(createNewSubscriptionPayload);
    },
    [
      UpdateProductSubscription,
      alert,
      handleResponse,
      navigate,
      organizationId,
      organizationName,
      subscriptionId,
      subscriptionType,
      t,
    ],
  );

  const onAddNewContractHandler = () => {
    const details: AddContractModalFormDetails = {
      organizationId,
    };
    UpdateModalState([ADD_CONTRACT_MODAL_NAME, details]);
  };

  const validateForm = (data: IEditAAOrganizationSubscriptionModel) =>
    validateEditProductSubscriptionForm(data, validateErrors(data));

  const initData = {
    [BasicSubscriptionFormFields.ProductType]: subscriptionType,
    ...subscriptionData,
  };

  return {
    validateForm,
    handleSubmit,
    UpdateModalState,
    organizationContracts,
    subscriptionEditing,
    dataLoading,
    updateOrganizationSubscription,
    initData,
    onAddNewContractHandler,
  };
};
